import { useEffect } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import {
  BarChart3,
  BookOpen,
  Globe2,
  Layers,
  Leaf,
  Users,
  Landmark,
  ListChecks,
  SlidersHorizontal,
  Database,
  Calculator,
  EyeOff,
  TrendingUp,
  RefreshCw,
  ShieldCheck,
  ClipboardCheck,
  AlertTriangle,
  CheckCircle2,
  type LucideIcon,
} from "lucide-react";
import { useTranslations } from "@/lib/translations";
import { usePageMeta } from "@/hooks/use-page-meta";
import { useJsonLd } from "@/hooks/use-json-ld";
import { buildBreadcrumbList } from "@/lib/breadcrumbs";

const sectionsEn = [
  {
    number: "1",
    title: "Introduction",
    content: `ESG Invest provides environmental, social, and governance (ESG) ratings designed to help investors evaluate sustainability risks, corporate practices, and long-term resilience of publicly listed companies across Middle East markets.\n\nThe ESG Invest methodology assesses companies using a structured framework that evaluates environmental management, social responsibility, and governance quality based on publicly available information. The objective is to provide a transparent and consistent analytical view of corporate sustainability performance that can support investment analysis and benchmarking.\n\nESG ratings produced by ESG Invest are analytical opinions based on available information and do not constitute investment advice or financial recommendations.`,
  },
  {
    number: "2",
    title: "Coverage",
    content: `The ESG Invest rating universe includes publicly listed companies across major Middle East capital markets.`,
    bullets: [
      "Saudi Arabia",
      "United Arab Emirates",
      "Qatar",
      "Kuwait",
      "Oman",
      "Bahrain",
      "Jordan",
      "Palestine",
      "Lebanon",
    ],
    after: `The ESG Invest database currently covers more than 880 listed companies across these markets. Coverage is expanded periodically as disclosure improves and new companies become listed.\n\nCompanies are selected for coverage based on factors including market relevance, liquidity, and availability of publicly disclosed ESG information.`,
  },
  {
    number: "3",
    title: "ESG Rating Framework",
    content: `The ESG Invest methodology evaluates companies across three core pillars:`,
    bullets: ["Environmental", "Social", "Governance"],
    after: `Each pillar captures a range of sustainability topics reflecting the risks and management practices relevant to corporate operations.\n\nThe framework incorporates 43 ESG criteria, which are customized by sector to reflect the sustainability challenges most relevant to each industry.`,
  },
  {
    number: "4",
    title: "Environmental Assessment",
    content: `The Environmental pillar evaluates how companies manage environmental impacts and natural resource use.\n\nKey environmental topics assessed include:`,
    bullets: [
      "Climate Strategy and Greenhouse Gas Emissions",
      "Energy Consumption and Energy Efficiency",
      "Water Management Practices",
      "Waste Management and Pollution Control",
      "Environmental Policies and Management Systems",
      "Environmental Targets and Performance Monitoring",
      "Environmental Disclosure and Transparency",
    ],
    after: `The relevance of these indicators varies across sectors depending on environmental risk exposure.`,
  },
  {
    number: "5",
    title: "Social Assessment",
    content: `The Social pillar evaluates how companies manage relationships with employees, customers, suppliers, and communities.\n\nKey social topics assessed include:`,
    bullets: [
      "Workforce Management and Employee Wellbeing",
      "Occupational Health and Safety",
      "Diversity and Inclusion",
      "Employee Development and Training",
      "Labor Practices and Human Rights Policies",
      "Customer Responsibility and Product Safety",
      "Supply Chain Responsibility",
      "Community Engagement and Social Impact",
    ],
    after: `These indicators help assess the quality of corporate social responsibility practices and workforce management.`,
  },
  {
    number: "6",
    title: "Governance Assessment",
    content: `The Governance pillar evaluates the effectiveness of corporate governance structures and oversight mechanisms.\n\nKey governance topics assessed include:`,
    bullets: [
      "Board Structure and Independence",
      "Board Diversity and Expertise",
      "Executive Compensation and Incentives",
      "Shareholder Rights and Transparency",
      "Business Ethics and Anti-Corruption Policies",
      "Risk Management and Internal Controls",
      "Disclosure and Reporting Practices",
    ],
    after: `Governance indicators focus on the mechanisms through which companies ensure accountability, transparency, and responsible decision-making.`,
  },
  {
    number: "7",
    title: "Indicator Framework",
    content: `The ESG Invest methodology incorporates 43 ESG criteria distributed across the Environmental, Social, and Governance pillars.\n\nThese criteria fall into several categories:`,
    bullets: [
      "Policy Indicators evaluating the presence and quality of ESG policies",
      "Management Indicators assessing systems and oversight mechanisms",
      "Performance Indicators reflecting quantitative ESG performance metrics",
      "Disclosure Indicators evaluating transparency and quality of public reporting",
    ],
    after: `Sector-specific adjustments are applied to ensure that the criteria reflect the most material ESG risks for each industry.`,
  },
  {
    number: "8",
    title: "Sector Customization",
    content: `ESG risks vary significantly across industries. To address this, the ESG Invest methodology applies sector-specific weighting and relevance adjustments.\n\nExamples include:`,
    bullets: [
      "Energy and Utilities companies are assessed more heavily on environmental and climate-related indicators",
      "Financial Institutions receive greater emphasis on governance and risk management indicators",
      "Industrial and Manufacturing companies are evaluated more closely on environmental impact and workplace safety",
    ],
    after: `Sector customization improves the relevance and comparability of ESG ratings across industries.`,
  },
  {
    number: "9",
    title: "Data Sources",
    content: `ESG Invest ratings are primarily based on publicly available information to ensure transparency and replicability.\n\nSources used in the assessment include:`,
    bullets: [
      "Annual Reports and Financial Statements",
      "Sustainability and ESG Reports",
      "Corporate Governance Disclosures",
      "Company Websites and Policy Documents",
      "Regulatory Filings and Stock Exchange Disclosures",
      "Public Sustainability Data and Performance Metrics",
    ],
    after: `Using publicly disclosed information ensures equal treatment of companies and transparency of the analytical process.`,
  },
  {
    number: "10",
    title: "Scoring Methodology",
    content: `Each ESG criterion is evaluated based on the strength of policies, management systems, performance indicators, and disclosure practices.\n\nIndividual criteria are scored and aggregated to produce pillar-level scores for:`,
    bullets: [
      "Environmental Performance",
      "Social Responsibility",
      "Governance Quality",
    ],
    after: `The pillar scores are then combined to generate an overall ESG score ranging from 0 to 100.\n\nHigher scores indicate stronger ESG governance structures, more robust sustainability practices, and higher levels of transparency.`,
  },
  {
    number: "11",
    title: "Treatment of Limited Disclosure",
    content: `In emerging markets, ESG disclosure practices vary significantly across companies.\n\nWhere relevant ESG information is not publicly available, the ESG Invest methodology may assign lower scores to reflect disclosure gaps.\n\nThis approach encourages improved transparency while maintaining comparability across companies.`,
  },
  {
    number: "12",
    title: "Interpretation of ESG Scores",
    content: `ESG scores represent the relative strength of a company's sustainability practices and governance structures.\n\nHigher scores generally reflect:`,
    bullets: [
      "Stronger ESG governance structures",
      "More comprehensive sustainability policies",
      "Evidence of ESG performance monitoring",
      "Higher levels of transparency and disclosure",
    ],
    after: `Lower scores may indicate limited disclosure, weaker governance systems, or limited implementation of sustainability practices.`,
  },
  {
    number: "13",
    title: "Rating Updates",
    content: `ESG ratings are updated periodically to reflect new corporate disclosures and developments.\n\nUpdates may occur following:`,
    bullets: [
      "Publication of Annual Reports",
      "Release of Sustainability or ESG Reports",
      "Significant Corporate Governance Changes",
      "Major ESG Controversies or Events",
      "New Regulatory Disclosures",
    ],
    after: `These updates ensure that ESG ratings remain aligned with the most recent publicly available information.`,
  },
  {
    number: "14",
    title: "Quality Assurance",
    content: `ESG Invest applies internal review procedures to ensure consistency and analytical quality.\n\nQuality assurance processes include:`,
    bullets: [
      "Internal analytical review of ratings",
      "Validation of data sources",
      "Consistency checks across companies and sectors",
      "Periodic methodology reviews",
    ],
    after: `These processes support the reliability and credibility of ESG ratings.`,
  },
  {
    number: "15",
    title: "Methodology Review",
    content: `The ESG Invest methodology is reviewed periodically to ensure alignment with evolving ESG standards, market practices, and regulatory expectations.\n\nMethodology updates may incorporate improvements in:`,
    bullets: [
      "Indicator coverage",
      "Sector relevance",
      "Data availability",
      "Analytical precision",
    ],
    after: `Material methodology changes are communicated to stakeholders.`,
  },
  {
    number: "16",
    title: "Disclaimer",
    content: `ESG Invest ratings represent analytical opinions regarding ESG performance based on publicly available information.\n\nThey are provided for informational purposes only and do not constitute investment advice, financial recommendations, or credit ratings.\n\nUsers of ESG Invest ratings should conduct their own analysis before making investment decisions.`,
    disclaimer: true,
  },
];

const sectionsAr = [
  {
    number: "1",
    title: "مقدمة",
    content: `توفر ESG Invest تصنيفات بيئية واجتماعية وحوكمة (ESG) مصممة لمساعدة المستثمرين على تقييم مخاطر الاستدامة والممارسات المؤسسية والمرونة طويلة الأمد للشركات المدرجة في أسواق الشرق الأوسط.\n\nتقيّم منهجية ESG Invest الشركات باستخدام إطار منظّم يتناول الإدارة البيئية والمسؤولية الاجتماعية وجودة الحوكمة بناءً على المعلومات المتاحة للعموم. والهدف هو تقديم رؤية تحليلية شفافة ومتسقة لأداء الاستدامة المؤسسية تدعم تحليل الاستثمار والمقارنة المعيارية.\n\nتصنيفات ESG الصادرة عن ESG Invest هي آراء تحليلية مبنية على المعلومات المتاحة، ولا تشكّل نصيحة استثمارية أو توصيات مالية.`,
  },
  {
    number: "2",
    title: "نطاق التغطية",
    content: `يشمل مجال تصنيف ESG Invest الشركات المدرجة في كبرى أسواق رأس المال في الشرق الأوسط.`,
    bullets: [
      "المملكة العربية السعودية",
      "الإمارات العربية المتحدة",
      "قطر",
      "الكويت",
      "عُمان",
      "البحرين",
      "الأردن",
      "فلسطين",
      "لبنان",
    ],
    after: `تغطي قاعدة بيانات ESG Invest حالياً أكثر من 880 شركة مدرجة في هذه الأسواق. يتوسع نطاق التغطية دورياً مع تحسّن مستوى الإفصاح وإدراج شركات جديدة.\n\nتُختار الشركات للتغطية بناءً على عوامل تشمل الأهمية في السوق والسيولة وتوافر معلومات ESG المُفصح عنها علناً.`,
  },
  {
    number: "3",
    title: "إطار تصنيف ESG",
    content: `تقيّم منهجية ESG Invest الشركات عبر ثلاثة محاور أساسية:`,
    bullets: ["البيئة", "المجتمع", "الحوكمة"],
    after: `يغطي كل محور مجموعة من موضوعات الاستدامة التي تعكس المخاطر وممارسات الإدارة ذات الصلة بالعمليات المؤسسية.\n\nيتضمن الإطار 43 معياراً لـ ESG تم تخصيصها حسب القطاع لتعكس تحديات الاستدامة الأكثر صلة بكل صناعة.`,
  },
  {
    number: "4",
    title: "التقييم البيئي",
    content: `يقيّم المحور البيئي كيفية إدارة الشركات للتأثيرات البيئية واستخدام الموارد الطبيعية.\n\nتشمل الموضوعات البيئية الرئيسية التي يتم تقييمها:`,
    bullets: [
      "استراتيجية المناخ وانبعاثات الغازات الدفيئة",
      "استهلاك الطاقة وكفاءتها",
      "ممارسات إدارة المياه",
      "إدارة النفايات والسيطرة على التلوث",
      "السياسات البيئية وأنظمة الإدارة",
      "الأهداف البيئية ورصد الأداء",
      "الإفصاح البيئي والشفافية",
    ],
    after: `تتباين أهمية هذه المؤشرات بين القطاعات بحسب درجة التعرض للمخاطر البيئية.`,
  },
  {
    number: "5",
    title: "التقييم الاجتماعي",
    content: `يقيّم المحور الاجتماعي كيفية إدارة الشركات لعلاقاتها مع الموظفين والعملاء والموردين والمجتمعات.\n\nتشمل الموضوعات الاجتماعية الرئيسية التي يتم تقييمها:`,
    bullets: [
      "إدارة القوى العاملة ورفاهية الموظفين",
      "الصحة والسلامة المهنية",
      "التنوع والشمول",
      "تطوير الموظفين والتدريب",
      "ممارسات العمل وسياسات حقوق الإنسان",
      "مسؤولية العملاء وسلامة المنتجات",
      "المسؤولية في سلسلة التوريد",
      "المشاركة المجتمعية والأثر الاجتماعي",
    ],
    after: `تساعد هذه المؤشرات على تقييم جودة ممارسات المسؤولية الاجتماعية للشركات وإدارة القوى العاملة.`,
  },
  {
    number: "6",
    title: "تقييم الحوكمة",
    content: `يقيّم محور الحوكمة فعالية هياكل حوكمة الشركات وآليات الرقابة.\n\nتشمل الموضوعات الرئيسية للحوكمة التي يتم تقييمها:`,
    bullets: [
      "هيكل مجلس الإدارة واستقلاليته",
      "تنوع مجلس الإدارة وخبرته",
      "تعويضات المديرين التنفيذيين وحوافزهم",
      "حقوق المساهمين والشفافية",
      "أخلاقيات الأعمال وسياسات مكافحة الفساد",
      "إدارة المخاطر والرقابة الداخلية",
      "ممارسات الإفصاح والتقارير",
    ],
    after: `تركز مؤشرات الحوكمة على الآليات التي تضمن من خلالها الشركات المساءلة والشفافية وصنع القرار المسؤول.`,
  },
  {
    number: "7",
    title: "إطار المؤشرات",
    content: `تتضمن منهجية ESG Invest 43 معياراً موزعة على محاور البيئة والمجتمع والحوكمة.\n\nتنتمي هذه المعايير إلى عدة فئات:`,
    bullets: [
      "مؤشرات السياسات: تقييم وجود سياسات ESG وجودتها",
      "مؤشرات الإدارة: تقييم الأنظمة وآليات الإشراف",
      "مؤشرات الأداء: تعكس مقاييس أداء ESG الكمية",
      "مؤشرات الإفصاح: تقييم الشفافية وجودة التقارير العامة",
    ],
    after: `تُطبَّق تعديلات خاصة بكل قطاع لضمان أن تعكس المعايير أبرز مخاطر ESG في كل صناعة.`,
  },
  {
    number: "8",
    title: "التخصيص القطاعي",
    content: `تتباين مخاطر ESG بشكل ملحوظ عبر الصناعات. ولمعالجة ذلك، تطبق منهجية ESG Invest أوزاناً وتعديلات ملاءمة خاصة بكل قطاع.\n\nتشمل الأمثلة على ذلك:`,
    bullets: [
      "تُقيَّم شركات الطاقة والمرافق بتركيز أكبر على المؤشرات البيئية والمتعلقة بالمناخ",
      "تحظى المؤسسات المالية بتأكيد أكبر على مؤشرات الحوكمة وإدارة المخاطر",
      "تُقيَّم الشركات الصناعية والتصنيعية بدقة أكبر على التأثير البيئي وسلامة بيئة العمل",
    ],
    after: `يُحسّن التخصيص القطاعي من صلة تصنيفات ESG وقابليتها للمقارنة عبر الصناعات.`,
  },
  {
    number: "9",
    title: "مصادر البيانات",
    content: `تستند تصنيفات ESG Invest بصورة رئيسية إلى المعلومات المتاحة للعموم لضمان الشفافية وإمكانية التكرار.\n\nتشمل المصادر المستخدمة في التقييم:`,
    bullets: [
      "التقارير السنوية والبيانات المالية",
      "تقارير الاستدامة وESG",
      "إفصاحات حوكمة الشركات",
      "مواقع الشركات ووثائق السياسات",
      "الملفات التنظيمية وإفصاحات البورصات",
      "بيانات الاستدامة العامة ومقاييس الأداء",
    ],
    after: `يضمن استخدام المعلومات المُفصح عنها علناً المعاملة المتساوية للشركات وشفافية العملية التحليلية.`,
  },
  {
    number: "10",
    title: "منهجية التسجيل",
    content: `يُقيَّم كل معيار من معايير ESG بناءً على قوة السياسات وأنظمة الإدارة ومؤشرات الأداء وممارسات الإفصاح.\n\nتُسجَّل المعايير الفردية وتُجمَّع للحصول على درجات على مستوى المحاور لـ:`,
    bullets: [
      "الأداء البيئي",
      "المسؤولية الاجتماعية",
      "جودة الحوكمة",
    ],
    after: `تُجمَّع درجات المحاور بعد ذلك لتوليد درجة ESG إجمالية تتراوح بين 0 و100.\n\nتشير الدرجات الأعلى إلى هياكل حوكمة ESG أقوى وممارسات استدامة أكثر متانة ومستويات أعلى من الشفافية.`,
  },
  {
    number: "11",
    title: "معالجة محدودية الإفصاح",
    content: `في الأسواق الناشئة، تتباين ممارسات إفصاح ESG بشكل ملحوظ بين الشركات.\n\nعند عدم توافر معلومات ESG ذات صلة للعموم، قد تمنح منهجية ESG Invest درجات أدنى للتعبير عن الفجوات في الإفصاح.\n\nيشجع هذا النهج على تحسين الشفافية مع الحفاظ على إمكانية المقارنة بين الشركات.`,
  },
  {
    number: "12",
    title: "تفسير درجات ESG",
    content: `تعكس درجات ESG القوة النسبية لممارسات الاستدامة وهياكل الحوكمة في الشركة.\n\nتعكس الدرجات الأعلى عموماً:`,
    bullets: [
      "هياكل حوكمة ESG أقوى",
      "سياسات استدامة أكثر شمولاً",
      "دليل على رصد أداء ESG",
      "مستويات أعلى من الشفافية والإفصاح",
    ],
    after: `قد تشير الدرجات الأدنى إلى محدودية الإفصاح أو ضعف أنظمة الحوكمة أو محدودية تطبيق ممارسات الاستدامة.`,
  },
  {
    number: "13",
    title: "تحديث التصنيفات",
    content: `تُحدَّث تصنيفات ESG دورياً لتعكس الإفصاحات المؤسسية والمستجدات الجديدة.\n\nقد تحدث التحديثات إثر:`,
    bullets: [
      "نشر التقارير السنوية",
      "إصدار تقارير الاستدامة أو ESG",
      "تغييرات جوهرية في حوكمة الشركات",
      "خلافات أو أحداث ESG كبرى",
      "إفصاحات تنظيمية جديدة",
    ],
    after: `تضمن هذه التحديثات توافق تصنيفات ESG مع أحدث المعلومات المتاحة للعموم.`,
  },
  {
    number: "14",
    title: "ضمان الجودة",
    content: `تطبق ESG Invest إجراءات مراجعة داخلية لضمان الاتساق والجودة التحليلية.\n\nتشمل عمليات ضمان الجودة:`,
    bullets: [
      "المراجعة التحليلية الداخلية للتصنيفات",
      "التحقق من مصادر البيانات",
      "فحوصات الاتساق عبر الشركات والقطاعات",
      "مراجعات المنهجية الدورية",
    ],
    after: `تدعم هذه العمليات موثوقية تصنيفات ESG ومصداقيتها.`,
  },
  {
    number: "15",
    title: "مراجعة المنهجية",
    content: `تخضع منهجية ESG Invest للمراجعة الدورية لضمان التوافق مع معايير ESG المتطورة وممارسات السوق والتوقعات التنظيمية.\n\nقد تتضمن تحديثات المنهجية تحسينات في:`,
    bullets: [
      "تغطية المؤشرات",
      "الملاءمة القطاعية",
      "توافر البيانات",
      "الدقة التحليلية",
    ],
    after: `تُبلَّغ التغييرات الجوهرية في المنهجية إلى أصحاب المصلحة.`,
  },
  {
    number: "16",
    title: "إخلاء المسؤولية",
    content: `تمثل تصنيفات ESG Invest آراءً تحليلية حول أداء ESG بناءً على المعلومات المتاحة للعموم.\n\nتُقدَّم لأغراض إعلامية فحسب، ولا تشكّل نصيحة استثمارية أو توصيات مالية أو تصنيفات ائتمانية.\n\nينبغي على مستخدمي تصنيفات ESG Invest إجراء تحليلاتهم الخاصة قبل اتخاذ أي قرارات استثمارية.`,
    disclaimer: true,
  },
];

type Theme = {
  bar: string;
  badge: string;
  icon: string;
  dot: string;
  chip: string;
  soft: string;
};

const themes: Record<string, Theme> = {
  indigo: {
    bar: "bg-indigo-500",
    badge: "bg-indigo-500 text-white",
    icon: "text-indigo-600 dark:text-indigo-400",
    dot: "text-indigo-500",
    chip: "bg-indigo-50 text-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-100",
    soft: "bg-indigo-50/70 dark:bg-indigo-950/20",
  },
  emerald: {
    bar: "bg-emerald-500",
    badge: "bg-emerald-500 text-white",
    icon: "text-emerald-600 dark:text-emerald-400",
    dot: "text-emerald-500",
    chip: "bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100",
    soft: "bg-emerald-50/70 dark:bg-emerald-950/20",
  },
  blue: {
    bar: "bg-blue-500",
    badge: "bg-blue-500 text-white",
    icon: "text-blue-600 dark:text-blue-400",
    dot: "text-blue-500",
    chip: "bg-blue-50 text-blue-900 dark:bg-blue-950/40 dark:text-blue-100",
    soft: "bg-blue-50/70 dark:bg-blue-950/20",
  },
  violet: {
    bar: "bg-violet-500",
    badge: "bg-violet-500 text-white",
    icon: "text-violet-600 dark:text-violet-400",
    dot: "text-violet-500",
    chip: "bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:text-violet-100",
    soft: "bg-violet-50/70 dark:bg-violet-950/20",
  },
  amber: {
    bar: "bg-amber-500",
    badge: "bg-amber-500 text-white",
    icon: "text-amber-600 dark:text-amber-400",
    dot: "text-amber-500",
    chip: "bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100",
    soft: "bg-amber-50/70 dark:bg-amber-950/20",
  },
  teal: {
    bar: "bg-teal-500",
    badge: "bg-teal-500 text-white",
    icon: "text-teal-600 dark:text-teal-400",
    dot: "text-teal-500",
    chip: "bg-teal-50 text-teal-900 dark:bg-teal-950/40 dark:text-teal-100",
    soft: "bg-teal-50/70 dark:bg-teal-950/20",
  },
  sky: {
    bar: "bg-sky-500",
    badge: "bg-sky-500 text-white",
    icon: "text-sky-600 dark:text-sky-400",
    dot: "text-sky-500",
    chip: "bg-sky-50 text-sky-900 dark:bg-sky-950/40 dark:text-sky-100",
    soft: "bg-sky-50/70 dark:bg-sky-950/20",
  },
  slate: {
    bar: "bg-slate-400",
    badge: "bg-slate-500 text-white",
    icon: "text-slate-600 dark:text-slate-300",
    dot: "text-slate-500",
    chip: "bg-slate-100 text-slate-800 dark:bg-slate-800/60 dark:text-slate-100",
    soft: "bg-slate-50 dark:bg-slate-900/40",
  },
};

const sectionMeta: Record<string, { icon: LucideIcon; theme: keyof typeof themes }> = {
  "1": { icon: BookOpen, theme: "indigo" },
  "2": { icon: Globe2, theme: "teal" },
  "3": { icon: Layers, theme: "indigo" },
  "4": { icon: Leaf, theme: "emerald" },
  "5": { icon: Users, theme: "blue" },
  "6": { icon: Landmark, theme: "violet" },
  "7": { icon: ListChecks, theme: "sky" },
  "8": { icon: SlidersHorizontal, theme: "amber" },
  "9": { icon: Database, theme: "teal" },
  "10": { icon: Calculator, theme: "amber" },
  "11": { icon: EyeOff, theme: "slate" },
  "12": { icon: TrendingUp, theme: "emerald" },
  "13": { icon: RefreshCw, theme: "sky" },
  "14": { icon: ShieldCheck, theme: "blue" },
  "15": { icon: ClipboardCheck, theme: "indigo" },
  "16": { icon: AlertTriangle, theme: "amber" },
};

const Methodology = () => {
  const { isRTL, language } = useTranslations();
  const sections = language === "ar" ? sectionsAr : sectionsEn;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  usePageMeta({
    title: "ESG Ratings Methodology — How We Score Middle East Companies | ESG Invest",
    description:
      "Learn how ESG Invest rates 880+ Middle East companies using 43 ESG criteria across Environmental, Social & Governance pillars. Transparent, sector-adjusted methodology.",
    path: "/methodology",
  });

  useJsonLd(
    "ld-methodology-breadcrumbs",
    buildBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "Methodology", path: "/methodology" },
    ]),
  );

  return (
    <Layout>
      <section className="relative text-white py-20 overflow-hidden" style={{ backgroundImage: 'url(/subscriptions-hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-black/60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
            <BarChart3 className="h-4 w-4 text-accent" />
            {language === "ar" ? "المنهجية" : "Methodology"}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {language === "ar" ? "منهجية تصنيفات ESG من ESG Invest" : "ESG Invest ESG Ratings Methodology"}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {language === "ar"
              ? "إطار شفاف ومتسق لتقييم الاستدامة المؤسسية لأكثر من 880 شركة مدرجة في الشرق الأوسط."
              : "A transparent and consistent framework for evaluating corporate sustainability across 880+ Middle East listed companies."}
          </p>
        </div>
      </section>

      <div className="bg-se-gray-50 dark:bg-background py-14">
        <div className={`max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 space-y-6 ${isRTL ? "text-right" : ""}`}>
          {sections.map((section) => {
            const meta = sectionMeta[section.number] ?? { icon: BarChart3, theme: "indigo" as const };
            const theme = themes[meta.theme];
            const Icon = meta.icon;

            if (section.disclaimer) {
              return (
                <div
                  key={section.number}
                  className="pt-2 text-xs text-muted-foreground/70 leading-relaxed space-y-2"
                  data-testid={`section-methodology-${section.number}`}
                >
                  <p className="font-medium text-muted-foreground">{section.title}</p>
                  {section.content?.split("\n\n").map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              );
            }

            return (
              <Card
                key={section.number}
                className="relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm transition-shadow hover:shadow-md"
                data-testid={`section-methodology-${section.number}`}
              >
                <div className={`absolute inset-y-0 ${isRTL ? "right-0" : "left-0"} w-1.5 ${theme.bar}`} />
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-4 mb-5">
                    <span className={`inline-flex items-center justify-center w-12 h-12 rounded-xl text-lg font-bold shadow-sm flex-shrink-0 ${theme.badge}`}>
                      {section.number}
                    </span>
                    <div className="flex items-center gap-2.5">
                      <Icon className={`h-5 w-5 flex-shrink-0 ${theme.icon}`} />
                      <h2 className="text-xl font-bold text-foreground">{section.title}</h2>
                    </div>
                  </div>

                  {section.content && (
                    <div className="space-y-3">
                      {section.content.split("\n\n").map((para, i) => (
                        <p key={i} className={`text-[15px] leading-relaxed ${section.disclaimer ? "text-muted-foreground/80 italic" : "text-muted-foreground"}`}>
                          {para}
                        </p>
                      ))}
                    </div>
                  )}

                  {section.bullets && (
                    <ul className="grid sm:grid-cols-2 gap-2.5 my-5">
                      {section.bullets.map((item) => (
                        <li key={item} className={`flex items-start gap-2.5 rounded-xl px-3.5 py-2.5 text-sm font-medium ${theme.chip}`}>
                          <CheckCircle2 className={`h-4 w-4 mt-0.5 flex-shrink-0 ${theme.dot}`} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.after && (
                    <div className={`space-y-2 rounded-xl px-4 py-3.5 ${theme.soft}`}>
                      {section.after.split("\n\n").map((para, i) => (
                        <p key={i} className="text-sm text-muted-foreground leading-relaxed">{para}</p>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default Methodology;
